import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Radio,
    RadioGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GeofenceFields, { DEFAULT_GEOFENCE } from './GeofenceFields';
import GuestListFields, { DEFAULT_GUEST_LIST, sanitizeGuestList } from './GuestListFields';
import RequiredProofsSelector from './RequiredProofsSelector';
import AuthRequiredForSelector from './AuthRequiredForSelector';
import { fallbackCopyTextToClipboard, getLinkPrefix } from '../utils/utils';
import { mutate } from 'swr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePolyAuth } from '../hooks/PolyAuthContext';
import { useAccounts } from '../hooks/useAccounts';
import { useAccountApps } from '../hooks/useApps';
import { isProofAllowed } from '../utils/proofFormatters';
import { useTranslation } from 'react-i18next';


const defaultProofs = [
    'name',
    'pg_region',
    'pg_attestation_key_id',
    'pg_photo_verification_url',
    'pg_presence',
    'pg_presence_scoring',
    'pg_high_certainty',
];

// Older callers pass `verifications` as a {proof: bool} map; normalize to
// the flat proof-key array the shared selector uses.
const toProofArray = (verifications) => {
    if (!verifications) return null;
    if (Array.isArray(verifications)) return verifications;
    return Object.entries(verifications)
        .filter(([, on]) => on)
        .map(([key]) => key);
};

// Inner table component for meeting creation UI and logic
export function CreateMeetingTable({
    accountId,
    setFeedback,
    handleSuccess,
    handleCancel,
    meetingTitle: meetingTitleProp,
    initialLinkData,
    linkUuid,
    isEditMode = false,
    linkType = 'msft_teams',
    listCacheKey = '/listteamslinks/',
}) {
    const { t } = useTranslation('shared');
    const [loading, setLoading] = useState(false);
    // If meetingTitleProp is set, use it, otherwise use state
    const [meetingTitle, setMeetingTitle] = useState(initialLinkData?.title || '');
    const [e2eeEnabled, setE2eeEnabled] = useState(initialLinkData?.e2ee || false);
    const [requiredProofs, setRequiredProofs] = useState(
        toProofArray(initialLinkData?.verifications ?? initialLinkData?.requiredProofs)
        || defaultProofs
    );
    const [geofence, setGeofence] = useState(initialLinkData?.geofence || null);
    const [guestList, setGuestList] = useState(initialLinkData?.guest_list || initialLinkData?.guestList || null);
    // Stored null means "inherit the App default". The radio shows the
    // resolved effective value; unless the user touches it (or the link
    // already stored an explicit value) the submit keeps sending null.
    const [authRequiredFor, setAuthRequiredFor] = useState('all');
    const [authDirty, setAuthDirty] = useState(false);
    const [autoArchive, setAutoArchive] = useState(true);
    const [autoArchiveDirty, setAutoArchiveDirty] = useState(false);
    const showAutoArchive = !isEditMode && (linkType === 'zoom' || linkType === 'msft_teams');
    // Sandbox meetings run on a shared tenant where every participant is
    // external, so the only workable setting is All Participants.
    const showApplyTo = linkType !== 'sandbox_teams';

    const flags = useFlags();
    const linkPrefix = getLinkPrefix(flags);
    const { api_client, accountId: pgAccountId } = usePolyAuth();
    const { accounts } = useAccounts();
    const allowedProofs = useMemo(() => {
        const acct = accounts?.find?.((a) => String(a.id) === String(pgAccountId));
        return acct?.extra_data?.allowed_proofs ?? null;
    }, [accounts, pgAccountId]);

    // Resolve the App that issues this link type so the Apply To radio can
    // display the inherited default. Missing means the backend default "all".
    const { apps } = useAccountApps();
    const issuingApp = useMemo(
        () => (apps || []).find((a) => a.link_type === linkType) || null,
        [apps, linkType]
    );
    const appDefaultAuth = issuingApp?.extra_data?.default_auth_required_for === 'external_only'
        ? 'external_only'
        : 'all';
    const appDefaultAutoArchive = issuingApp?.default_auto_archive !== false;

    const storedAuth =
        (initialLinkData?.authRequiredFor ?? initialLinkData?.auth_required_for) || null;

    // Track the resolved effective value until the user touches the radio.
    useEffect(() => {
        if (!authDirty) {
            setAuthRequiredFor(storedAuth || appDefaultAuth);
        }
    }, [storedAuth, appDefaultAuth, authDirty]);

    useEffect(() => {
        if (!autoArchiveDirty) {
            setAutoArchive(appDefaultAutoArchive);
        }
    }, [appDefaultAutoArchive, autoArchiveDirty]);

    // Update state when initialLinkData changes (for edit mode)
    useEffect(() => {
        if (initialLinkData) {
            if (!meetingTitleProp) {
                setMeetingTitle(initialLinkData.title || '');
            }
            setE2eeEnabled(initialLinkData.e2ee || false);
            setRequiredProofs(
                toProofArray(initialLinkData.verifications ?? initialLinkData.requiredProofs)
                || defaultProofs
            );
            setGeofence(initialLinkData.geofence || null);
            setGuestList(initialLinkData.guest_list || initialLinkData.guestList || null);
            setAuthDirty(false);
        }
    }, [initialLinkData, meetingTitleProp]);

    const handleProofsChange = (proofs) => {
        setRequiredProofs(proofs);
        // Seed the inline editors when their proof turns on.
        if (proofs.includes('pg_region_geofence') && !geofence) setGeofence(DEFAULT_GEOFENCE);
        if (proofs.includes('pg_guest_list') && !guestList) setGuestList(DEFAULT_GUEST_LIST);
    };

    const didCancel = useCallback(() => {
        setMeetingTitle('');
        setE2eeEnabled(false);
        setRequiredProofs(defaultProofs);
        setGeofence(null);
        setGuestList(null);
        setAuthDirty(false);
        setAutoArchiveDirty(false);
        handleCancel();
    }, [handleCancel]);

    const createLink = useCallback( async (event) => {
        setLoading(true);
        const actionMessage = isEditMode ? t('createMeeting.updating') : t('createMeeting.creating');
        setFeedback && setFeedback({ type: 'info', message: actionMessage });

        // Drop any selected proofs the account cap bars.
        const selectedProofs = requiredProofs.filter((k) => isProofAllowed(k, allowedProofs));

        // null = inherit the App default; explicit only when stored or chosen.
        const authRequiredForPayload = authDirty || storedAuth ? authRequiredFor : null;

        const geofencePayload = selectedProofs.includes('pg_region_geofence')
            ? (geofence || DEFAULT_GEOFENCE)
            : undefined;

        const guestListPayload = selectedProofs.includes('pg_guest_list')
            ? sanitizeGuestList(guestList)
            : undefined;

        try {
            let partResponse;
            if (isEditMode && linkUuid) {
                // Update existing link (e2ee changes disabled for now)
                partResponse = await api_client.patch(`/v2/${linkUuid}`, {
                    title: meetingTitleProp ?? meetingTitle,
                    requiredProofs: selectedProofs,
                    authRequiredFor: authRequiredForPayload,
                    ...(geofencePayload ? { geofence: geofencePayload } : {}),
                    ...(guestListPayload ? { guestList: guestListPayload } : {}),
                    // e2ee: e2eeEnabled - disabled for now, requires more API work
                });
            } else {
                // Create new link
                partResponse = await api_client.put(`/v2/link/type/${linkType}`, {
                    accountId: accountId,
                    title: meetingTitleProp ?? meetingTitle,
                    requiredProofs: selectedProofs,
                    authRequiredFor: authRequiredForPayload,
                    e2ee: e2eeEnabled,
                    ...(geofencePayload ? { geofence: geofencePayload } : {}),
                    ...(guestListPayload ? { guestList: guestListPayload } : {}),
                    // Null defers to the App's default_auto_archive, the same
                    // way authRequiredFor above defers to its App default.
                    ...(showAutoArchive ? { autoArchive: autoArchiveDirty ? autoArchive : null } : {}),
                });
            }

            const partResponseJson = await partResponse.data;
            const newLink = partResponseJson.link;
            const baseCacheKey = listCacheKey.replace(/\/$/, '');
            await mutate(
                key => typeof key === 'string' && key.startsWith(baseCacheKey),
                (currentData) => {
                    if (!currentData || isEditMode || !newLink) return currentData;
                    const existingLinks = currentData.links || currentData;
                    const updatedLinks = Array.isArray(existingLinks) ? [newLink, ...existingLinks] : existingLinks;
                    return currentData.links
                        ? { ...currentData, links: updatedLinks, pagination: currentData.pagination ? { ...currentData.pagination, total: (currentData.pagination.total || 0) + 1 } : undefined }
                        : updatedLinks;
                },
                { revalidate: false }
            );
            
            setMeetingTitle('');
            setE2eeEnabled(false);
            setRequiredProofs(defaultProofs);
            setGeofence(null);
            setGuestList(null);
            setAuthRequiredFor('external_only');
            // Clearing the flag re-seeds from the App default; hardcoding true
            // here would pin one-time on every later create in this session,
            // because the side pane keeps this component mounted.
            setAutoArchiveDirty(false);

            if (isEditMode) {
                handleSuccess(event, null);
                setFeedback && setFeedback({ type: 'success', message: t('createMeeting.updatedSuccess') });
            } else {
                handleSuccess(event, partResponseJson.url);
                fallbackCopyTextToClipboard(partResponseJson.url);
                setFeedback && setFeedback({ type: 'success', message: t('createMeeting.copiedToClipboard') });
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} meeting link:`, error);
            // api_client goes through the step-up interceptor: a cancelled
            // verification rejects with code 'step_up_required'.
            const errorMessage = error?.code === 'step_up_required'
                ? t('common.verificationRequired')
                : (isEditMode ? t('createMeeting.updateFailed') : t('createMeeting.createFailed'));
            setFeedback && setFeedback({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    }, [accountId, allowedProofs, e2eeEnabled, authRequiredFor, authDirty, storedAuth, autoArchive, autoArchiveDirty, showAutoArchive, handleSuccess, meetingTitle, meetingTitleProp, setFeedback, requiredProofs, geofence, guestList, api_client, isEditMode, linkUuid, linkType, listCacheKey]);

    return (
        <>
            {meetingTitleProp ? (
                <Typography variant="h6" sx={{ }}>{t('createMeeting.topicPrefix', { title: meetingTitleProp })}</Typography>
            ) : (
                <TextField
                    autoFocus
                    margin="dense"
                    label={t('createMeeting.meetingTitleLabel')}
                    type="text"
                    fullWidth
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && meetingTitle.trim() && !loading) {
                            e.preventDefault();
                            createLink(e);
                        }
                    }}
                    sx={{ mt: 1 }}
                />
            )}
            {showAutoArchive && (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                        {t('createMeeting.meetingType')}
                    </Typography>
                    <RadioGroup
                        value={autoArchive ? 'oneTime' : 'recurring'}
                        onChange={(e) => {
                            setAutoArchiveDirty(true);
                            setAutoArchive(e.target.value === 'oneTime');
                        }}
                    >
                        <FormControlLabel
                            value="oneTime"
                            control={<Radio size="small" />}
                            label={
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2">{t('createMeeting.oneTime')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('createMeeting.oneTimeDesc')}
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="recurring"
                            control={<Radio size="small" />}
                            label={
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2">{t('createMeeting.recurring')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('createMeeting.recurringDesc')}
                                    </Typography>
                                </Box>
                            }
                        />
                    </RadioGroup>
                </Box>
            )}
            <Accordion sx={{ mt: 1 }} defaultExpanded={isEditMode}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="advanced-options-content"
                    id="advanced-options-header"
                >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('createMeeting.verificationOptions')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <RequiredProofsSelector
                        value={requiredProofs}
                        onChange={handleProofsChange}
                        disabled={loading}
                        allowedProofs={allowedProofs}
                        geofenceSlot={
                            <GeofenceFields value={geofence} onChange={setGeofence} disabled={loading} />
                        }
                        guestListSlot={
                            <GuestListFields value={guestList} onChange={setGuestList} disabled={loading} />
                        }
                        applyToSlot={showApplyTo ? (
                            <AuthRequiredForSelector
                                value={authRequiredFor}
                                onChange={(value) => {
                                    setAuthRequiredFor(value);
                                    setAuthDirty(true);
                                }}
                                disabled={loading}
                            />
                        ) : null}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={e2eeEnabled} onChange={(e) => setE2eeEnabled(e.target.checked)} disabled={isEditMode} />}
                        label={
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('createMeeting.enableE2EE')}</Typography>
                                <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                    {isEditMode
                                        ? t('createMeeting.e2eeCannotChange')
                                        : t('createMeeting.e2eeNote')}
                                </Typography>
                            </Box>
                        }
                        sx={{ mt: 2 }}
                    />
                </AccordionDetails>
            </Accordion>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={didCancel} sx={{ mr: 2 }}>{t('createMeeting.cancel')}</Button>
                <Button
                    onClick={createLink}
                    variant="contained"
                    disabled={
                        (!meetingTitleProp && !meetingTitle.trim()) ||
                        loading ||
                        // An empty guest list would lock everyone out (server fails closed).
                        (requiredProofs.includes('pg_guest_list') && sanitizeGuestList(guestList).entries.length === 0)
                    }
                >
                    {loading ? <CircularProgress size={24} /> : (isEditMode ? t('createMeeting.updateMeeting') : t('createMeeting.createMeeting'))}
                </Button>
            </Box>
        </>

    );
};


// Outer dialog-centric component
export default function CreateMeetingDialog({
    open, onClose, accountId, setFeedback, meetingTitle, linkType, listCacheKey,
    initialLinkData, linkUuid, isEditMode = false,
}) {
    const { t } = useTranslation('shared');
    const handleSuccess = useCallback( (event, meetingURL) => {
        console.log("handleSuccess called with event and meetingURL");
        onClose(event, meetingURL);
    }, [onClose]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditMode ? t('linkList.editMeeting') : t('createMeeting.dialogTitle')}</DialogTitle>
            <DialogContent sx={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                overflow: 'auto'
            }}>
                <CreateMeetingTable
                    accountId={accountId}
                    setFeedback={setFeedback}
                    handleSuccess={handleSuccess}
                    handleCancel={handleCancel}
                    meetingTitle={meetingTitle}
                    linkType={linkType}
                    listCacheKey={listCacheKey}
                    initialLinkData={initialLinkData}
                    linkUuid={linkUuid}
                    isEditMode={isEditMode}
                />
            </DialogContent>
        </Dialog>   
    );
};
